import moment from 'moment-timezone';

// Flask invoice timestamps are stored as UTC. Laravel invoice responses already
// contain salon-local clocks. Keep the basis explicit so repeated normalization
// (including a sales invoice opened from a client profile) cannot shift twice.
export const normalizeInvoiceAppointmentTimes = (invoice) => {
  const zone = invoice.salon?.timezone;
  const sourceZone = invoice.appointment_datetime_timezone;
  const convert = sourceZone && zone && moment.tz.zone(sourceZone) && moment.tz.zone(zone);
  const localTime = (value) => {
    if (!convert || !value) return value;
    const parsed = moment.tz(value, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true, sourceZone);
    return parsed.isValid() ? parsed.tz(zone).format('YYYY-MM-DD HH:mm:ss') : value;
  };
  const appointment = (value) => {
    if (!value || typeof value !== 'object') return value;
    const start = localTime(value.start_datetime);
    const end = localTime(value.end_datetime);
    return {
      ...value,
      ...(start ? { start_datetime: start } : {}),
      ...(end ? { end_datetime: end } : {}),
      ...(convert && start ? { dateof: start.slice(0, 10), start_time: start.slice(11, 19) } : {}),
      ...(convert && end ? { end_time: end.slice(11, 19) } : {}),
      ...(Array.isArray(value.addonservices) ? { addonservices: value.addonservices.map(appointment) } : {}),
      ...(Array.isArray(value.addanotherservices) ? { addanotherservices: value.addanotherservices.map(appointment) } : {}),
    };
  };
  const appointments = Array.isArray(invoice.appointments) ? invoice.appointments.map(appointment) : [];
  const byId = new Map();
  const candidates = [];
  const index = (row, type = "appointment") => {
    candidates.push({row, type});
    byId.set(String(row.id), row);
    (row.addonservices || []).forEach(child => index(child, "addonservices"));
    (row.addanotherservices || []).forEach(child => index(child, "addanotherservices"));
  };
  appointments.forEach(row => index(row));
  const isAppointmentLine = (line) => ['appointment', 'addonservices', 'addanotherservices'].includes(String(line.type || line.item?.type || '').toLowerCase());
  const appointmentLines = (Array.isArray(invoice.cart) ? invoice.cart : []).filter(isAppointmentLine);
  const cart = Array.isArray(invoice.cart) ? invoice.cart.map((line) => {
    const timedLine = appointment(line);
    const embedded = appointment(line.appointment);
    const explicitId = line.appointment?.id ?? line.appointment_id;
    // Legacy cart item IDs are service IDs only when the API explicitly says so.
    // Never infer this from a coincidental number or the ordering of the rows.
    const hasServiceIdentity = line.service_id !== undefined && line.service_id !== null;
    const type = String(line.type || line.item?.type || "").toLowerCase();
    const serviceMatches = hasServiceIdentity ? candidates.filter(candidate =>
      candidate.type === type && String(candidate.row.service_id) === String(line.service_id)
      && (!line.staff_id || String(candidate.row.staff_id ?? candidate.row.staff?.id) === String(line.staff_id))) : [];
    const matched = isAppointmentLine(line) && (explicitId !== undefined && explicitId !== null
      ? byId.get(String(explicitId))
      : hasServiceIdentity
        ? (serviceMatches.length === 1 ? serviceMatches[0].row : null)
        : byId.get(String(line.item?.id ?? line.item_id))
          || (appointments.length === 1 && appointmentLines.length === 1 ? appointments[0] : null));
    return { ...timedLine, ...(matched || embedded ? { appointment: matched || embedded } : {}) };
  }) : invoice.cart;
  return {
    ...invoice,
    ...(convert ? { appointment_datetime_timezone: zone } : {}),
    ...(Array.isArray(invoice.appointments) ? { appointments } : {}),
    ...(invoice.appointment ? { appointment: appointment(invoice.appointment) } : {}),
    ...(cart ? { cart } : {}),
  };
};
